import { useCallback, useEffect } from 'react';
import {
	type Edge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { PARTICIPANT_DND_TYPE } from '../../../common/constants';
import { useStandupStore } from '../../store';

export const useParticipantListDragAndDrop = () => {
	const [{ participants }, { reorderParticipant, toggleParticipantStatus }] = useStandupStore();

	const reorder = useCallback(
		({
			startIndex,
			indexOfTarget,
			closestEdgeOfTarget,
		}: {
			startIndex: number;
			indexOfTarget: number;
			closestEdgeOfTarget: Edge | null;
		}) => {
			const finishIndex = getReorderDestinationIndex({
				startIndex,
				indexOfTarget,
				closestEdgeOfTarget,
				axis: 'vertical',
			});

			if (finishIndex === startIndex) {
				return;
			}

			reorderParticipant({ startIndex, finishIndex });
		},
		[reorderParticipant],
	);

	/*
	See pragmatic-drag-and-drop element adapter docs for details on implementation
	https://atlassian.design/components/pragmatic-drag-and-drop/core-package/adapters/element/about

	Mostly adapted from the list example https://atlassian.design/components/pragmatic-drag-and-drop/examples#list
	*/
	useEffect(
		() =>
			monitorForElements({
				canMonitor({ source }) {
					return source.data.type === PARTICIPANT_DND_TYPE;
				},
				onDrop({ location, source }) {
					const target = location.current.dropTargets[0];
					if (!target) {
						return;
					}

					const startIndex = participants.findIndex(
						(participant) => participant.id === source.data.id,
					);

					const indexOfTarget = participants.findIndex(
						(participant) => participant.id === target.data.id,
					);

					const closestEdgeOfTarget = extractClosestEdge(target.data);

					if (source.data.status !== target.data.status && typeof source.data.id === 'string') {
						toggleParticipantStatus(source.data.id);
					}

					reorder({
						startIndex,
						indexOfTarget,
						closestEdgeOfTarget,
					});
				},
			}),
		[participants, reorder, toggleParticipantStatus],
	);
};
